import type {RefObject} from 'react';
import {useCallback, useEffect, useRef} from 'react';

/**
 * Custom hook to detect clicks outside a specified element and trigger a close function.
 *
 * @param close - Function to be called when an outside click is detected.
 * @returns A ref to be attached to the target element.
 */
const useCloseOnOutsideClick = <T extends HTMLElement>(
  close: () => void,
): RefObject<T> => {
  const node = useRef<T>(null);

  const closeHandler = useCallback(
    (evt: MouseEvent) => {
      const target = evt.target as Node;
      // The date-picker calendar is portaled to body, outside the element;
      // closing on its mousedown would unmount it before the click lands.
      const inDatePicker =
        target instanceof Element && target.closest('#datepicker-portal');
      if (node.current && !node.current.contains(target) && !inDatePicker) {
        close?.();
      }
    },
    [close],
  );

  useEffect(() => {
    document.addEventListener('mousedown', closeHandler);
    return () => {
      document.removeEventListener('mousedown', closeHandler);
    };
  }, [close, closeHandler]);

  return node;
};

export default useCloseOnOutsideClick;
